/**
 * The cashier's allowance for money given back (Phase 7B-5, ADR-035). Ships off.
 *
 * Off, every refund and payment void by a cashier needs the owner, as before. On, an employee holding
 * `refund.allowance` (CASHIER by default) gives money back without the owner when, with this one, the order's money
 * given back on the allowance stays within the per-order limit, the cashier's in the business day within the daily
 * limit (every shift: reopening does not reset it), the money goes back the way it was paid, and nothing else happened
 * on the order. Refunds and payment voids share the one allowance. The server decides under the cashier's day lock;
 * these helpers mirror `app.giveback_assess` for the screens and the tests.
 */

/** Why the owner must approve. Mirrors the `why` codes of `app.giveback_assess`. */
export const GIVE_BACK_WHY = [
  'not_allowed',
  'over_order_max',
  'over_day_total',
  'other_method',
  'history',
] as const;
export type GiveBackWhy = (typeof GIVE_BACK_WHY)[number];

export const GIVE_BACK_WHY_LABEL: Record<GiveBackWhy, string> = {
  not_allowed: 'The cashier’s allowance is off',
  over_order_max: 'More than a cashier gives back alone on one order',
  over_day_total: 'Over what this cashier gives back alone in a day',
  other_method: 'Money goes back another way than it was paid',
  history:
    'The order had a write-off, a return, a correction, a flagged reference or a payment the owner’s check did not find',
};

/** The usage read when money was given back on the allowance; stored with each row (`reversal.allowance_usage`). */
export interface GiveBackUsage {
  business_date: string;
  order_given_minor: number;
  order_max_minor: number;
  day_given_minor: number;
  day_max_minor: number;
}

/** `app.giveback_assess`: whether money given back is within the allowance, and why not. */
export interface GiveBackAssessment {
  needs_owner: boolean;
  why: GiveBackWhy[];
  amount_minor: number;
  usage: GiveBackUsage;
}

/** The owner's settings (refund.*; owner-only). */
export interface CashierAllowanceLimits {
  enabled: boolean;
  orderMaxMinor: number;
  dayMaxMinor: number;
}

/** As approved: off, ₱200 an order and ₱500 a cashier a day. */
export const DEFAULT_CASHIER_ALLOWANCE: CashierAllowanceLimits = {
  enabled: false,
  orderMaxMinor: 20_000,
  dayMaxMinor: 50_000,
};

export interface GiveBackInput {
  /** The actor holds `refund.allowance`. */
  holdsPermission: boolean;
  amountMinor: number;
  /** Given back on the allowance on this order so far, by every cashier. */
  orderGivenMinor: number;
  /** Given back on the allowance by this cashier in this business day, every shift. */
  dayGivenMinor: number;
  otherMethod: boolean;
  history: boolean;
}

/** Mirrors `app.giveback_assess`: the reasons, in the server's order. */
export function giveBackWhy(limits: CashierAllowanceLimits, input: GiveBackInput): GiveBackWhy[] {
  const why: GiveBackWhy[] = [];
  if (!limits.enabled || !input.holdsPermission) why.push('not_allowed');
  if (input.orderGivenMinor + input.amountMinor > limits.orderMaxMinor) why.push('over_order_max');
  if (input.dayGivenMinor + input.amountMinor > limits.dayMaxMinor) why.push('over_day_total');
  if (input.otherMethod) why.push('other_method');
  if (input.history) why.push('history');
  return why;
}

/** What is left of the cashier's day after money given back on the allowance (never below zero). */
export function allowanceLeftToday(usage: GiveBackUsage, givenMinor: number): number {
  return Math.max(0, usage.day_max_minor - usage.day_given_minor - givenMinor);
}
